import { useEffect } from 'react';
import { useHostBranding } from './useHostBranding';

/**
 * Downscale a remote image to a square PNG data-URI (transparent-padded, aspect
 * preserved) for use as a favicon. Returns null if the image can't be loaded or
 * the canvas is tainted (cross-origin without CORS) — the caller then falls back
 * to the raw URL. A short timeout guards against an image that never loads.
 */
async function downscaleToFaviconDataUri(src: string, size = 64): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('favicon image load timeout')), 5000);
      img.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error('favicon image load error'));
      };
      img.src = src;
    });
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx || !img.naturalWidth || !img.naturalHeight) return null;
    const scale = Math.min(size / img.naturalWidth, size / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Replace the browser-tab favicon by swapping the <link rel="icon"> ELEMENT
 * (remove the old, insert a new one) rather than mutating its href. Safari and
 * Firefox often ignore an in-place href change on the existing icon link but
 * honor a freshly-inserted element — so element replacement is what actually
 * repaints the tab icon cross-browser.
 */
function replaceFavicon(href: string, type?: string): void {
  document.head.querySelectorAll("link[rel~='icon']").forEach((l) => l.remove());
  const link = document.createElement('link');
  link.setAttribute('rel', 'icon');
  if (type) link.setAttribute('type', type);
  link.setAttribute('href', href);
  document.head.appendChild(link);
}

/**
 * useHostBrandedTab — applies HOSTNAME-based tenant branding to the browser
 * tab (document.title + favicon) once, at the app root.
 *
 * The page hostname is the same before and after login (a logged-in EduNova
 * user is still on edunova.yudulearning.com), so a single host-driven effect
 * brands the tab on the LOGIN screen and keeps it branded inside the app —
 * unlike the auth-only path (MasterLayout/useOrgBranding) which never runs
 * pre-login and only swaps the favicon when an org has a dedicated
 * `favicon_url` (which tenants branded by logo alone, e.g. EduNova, do not).
 *
 * The favicon is a DOWNSCALED data-URI of the tenant's `logo_url` (already
 * returned by /api/public/org-branding) — small, crisp, and honored even by
 * browsers that ignore a remote-URL favicon swap. Unbranded hosts (localhost,
 * Vercel previews, the raw *.vercel.app) resolve to null fields, so the neutral
 * static index.html title/favicon are left untouched.
 */
export function useHostBrandedTab(): void {
  const { orgName, logoUrl, faviconUrl } = useHostBranding();

  // Tab title → tenant name (once it resolves; neutral default stays otherwise).
  useEffect(() => {
    if (orgName) document.title = orgName;
  }, [orgName]);

  // Tab favicon → the org's DEDICATED tab icon when set (a square emblem),
  // otherwise the logo. The logo can be a wide wordmark that squashes into an
  // illegible square, which is exactly why favicon_url exists — an admin can
  // upload a proper square icon for the tab independent of the login logo.
  // Downscaled to 64px and applied by element replacement; falls back to the
  // raw URL if the cross-origin canvas draw is blocked.
  const faviconSource = faviconUrl ?? logoUrl;
  useEffect(() => {
    if (!faviconSource) return;
    let cancelled = false;
    (async () => {
      const dataUri = await downscaleToFaviconDataUri(faviconSource, 64);
      if (cancelled) return;
      replaceFavicon(dataUri ?? faviconSource, dataUri ? 'image/png' : undefined);
    })();
    return () => {
      cancelled = true;
    };
  }, [faviconSource]);
}
